import { format, parseISO } from 'date-fns';
import { Button, Table } from 'rsuite';
import { SortType } from '../../hooks/useCampaign';

import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { IoMdEye } from 'react-icons/io';
import { MdDelete, MdEdit } from 'react-icons/md';
import type { TableColumnProps } from 'rsuite/lib/Table/TableColumn';
import { BadgeStatus, Status } from '../Components/BadgeStatus/BadgeStatus';
import styles from './TableCampaignRegister.module.scss';

type TAlign = 'left' | 'center' | 'right';

export const ColumnsTableCampaignRegister = [
  {
    sortable: true,
    align: 'left' as TAlign,
    fixed: false,
    headerCell: 'Nome da Campanha',
    dataKey: 'name',
    flexGrow: 3,
  },
  {
    sortable: true,
    align: 'left' as TAlign,
    fixed: false,
    headerCell: 'Tipo da Campanha',
    dataKey: 'campaign_type',
    flexGrow: 2,
  },
  {
    sortable: true,
    align: 'center' as TAlign,
    fixed: false,
    headerCell: 'Data criação',
    dataKey: 'created_date',
    flexGrow: 1,
  },
  {
    sortable: true,
    align: 'center' as TAlign,
    fixed: false,
    headerCell: 'Data início',
    dataKey: 'starting_date',
    flexGrow: 1,
  },
  {
    sortable: true,
    align: 'center' as TAlign,
    fixed: false,
    headerCell: 'Data fim',
    dataKey: 'expiration_date',
    flexGrow: 1,
  },
  {
    sortable: true,
    align: 'left',
    fixed: false,
    headerCell: 'Responsável',
    dataKey: 'author',
    flexGrow: 2,
  },
  {
    sortable: true,
    align: 'left' as TAlign,
    fixed: false,
    headerCell: 'Status',
    dataKey: 'status',
    flexGrow: 1,
  },
  {
    sortable: false,
    align: 'left' as TAlign,
    fixed: 'right',
    headerCell: '',
    dataKey: '',
    width: 120,
  },
];

const Column = Table.Column as React.ComponentType<React.PropsWithChildren<TableColumnProps>>;
const HeaderCell = Table.HeaderCell;
const Cell = Table.Cell;
const Pagination = Table.Pagination;

interface IRowData {
  name: string;
  campaign_id: string;
  campaign_type_order: number;
  campaign_type: string;
  created_date: string;
  starting_date: string;
  expiration_date: string;
  author: string;
  status: string;
}

interface ITableCampaignRegisterProps {
  editCampanha: (id: string) => void;
  removeCampanha: (id: string) => void;
  campaigns: {
    content: IRowData[];
    number: number;
    size: number;
    totalElements: number;
  } | null;
  sort: {
    type: SortType;
    orderBy: 'asc' | 'desc';
  };
  setSort: (value: { type: SortType; orderBy: 'asc' | 'desc' }) => void;
  setPage: (value: number) => void;
  setSize: (value: number) => void;
}

const customCellRenderers: Record<string, (rowData: IRowData) => React.ReactNode> = {
  status: (rowData: IRowData) => (rowData.status ? <BadgeStatus status={rowData.status as Status} /> : '-'),
  campaign_type: (rowData: IRowData) =>
    rowData.campaign_id ? (
      <div className={styles.campaign_type}>
        <span>#{rowData.campaign_type_order}</span>
        <span>{rowData.campaign_type}</span>
      </div>
    ) : (
      '-'
    ),
  created_date: (rowData: IRowData) => (rowData.created_date ? format(parseISO(rowData.created_date), 'dd/MM/yyyy') : '--'),
  starting_date: (rowData: IRowData) => (rowData.starting_date ? format(parseISO(rowData.starting_date), 'dd/MM/yyyy') : '--'),
  expiration_date: (rowData: IRowData) => (rowData.expiration_date ? format(parseISO(rowData.expiration_date), 'dd/MM/yyyy') : '--'),
};

export const TableCampaignRegister = ({ editCampanha, removeCampanha, campaigns, sort, setSort, setPage, setSize }: ITableCampaignRegisterProps) => {
  const [tableKey, setTableKey] = useState(0);
  const tableRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setTableKey(prevKey => prevKey + 1);
    }, 50);

    return () => clearTimeout(timer);
  }, []);

  if (!campaigns?.content?.length) {
    return <div className={styles.empty}>Nenhuma campanha encontrada</div>;
  }

  return (
    <section className={styles.container} ref={tableRef}>
      <Table
        key={tableKey}
        className="table"
        data={campaigns?.content || []}
        autoHeight
        sortColumn={sort.type}
        sortType={sort.orderBy}
        onSortColumn={(sortColumn, sortType) => {
          setSort({
            type: sortColumn as SortType,
            orderBy: sortType,
          });
        }}
      >
        {ColumnsTableCampaignRegister.map(col => {
          const hasKeyData = col.dataKey && col.dataKey !== '';

          return !hasKeyData ? (
            <Column width={120} fixed="right" key={col.dataKey}>
              <HeaderCell />

              <Cell>
                {(rowData: IRowData) => (
                  <span className={classNames(styles.button)}>
                    <Button
                      title={rowData.status === 'CURRENT' || rowData.status === 'CLOSED' ? 'Visualizar' : 'Editar'}
                      size="md"
                      onClick={() => editCampanha(rowData.campaign_id)}
                    >
                      {rowData.status === 'CURRENT' || rowData.status === 'CLOSED' ? <IoMdEye /> : <MdEdit />}
                    </Button>
                    <Button
                      title="Deletar"
                      size="md"
                      onClick={() => removeCampanha(rowData.campaign_id)}
                      disabled={rowData.status === 'CURRENT' || rowData.status === 'CLOSED'}
                    >
                      <MdDelete />
                    </Button>
                  </span>
                )}
              </Cell>
            </Column>
          ) : (
            <Column
              flexGrow={col.flexGrow}
              sortable={col.sortable}
              align={col.align as TAlign}
              fixed={col.fixed as 'left' | 'right' | false}
              width={col.width}
              key={col.dataKey}
            >
              <HeaderCell>{col.headerCell}</HeaderCell>
              {customCellRenderers[col.dataKey] ? (
                <Cell dataKey={col.dataKey}>
                  {(rowData: IRowData) => (
                    <div className={rowData.status === 'CLOSED' ? styles['closed-campaign'] : ''}>{customCellRenderers[col.dataKey](rowData)}</div>
                  )}
                </Cell>
              ) : (
                <Cell dataKey={col.dataKey}>
                  {(rowData: IRowData) => (
                    <div className={rowData.status === 'CLOSED' ? styles['closed-campaign'] : ''}>{rowData[col.dataKey as keyof IRowData] || '-'}</div>
                  )}
                </Cell>
              )}
            </Column>
          );
        })}
      </Table>
      <Pagination
        lengthMenu={[
          {
            value: 20,
            label: 20,
          },
          {
            value: 40,
            label: 40,
          },
        ]}
        activePage={(campaigns?.number ?? 0) + 1}
        displayLength={campaigns?.size ?? 0}
        total={campaigns?.totalElements ?? 0}
        onChangePage={value => setPage(value - 1)}
        onChangeLength={value => setSize(value)}
      />
    </section>
  );
};
